/**
 * This file is used to maintain synchronization between the user's login
 * status from Clerk with the redux login status.
 */

import {useEffect, useRef} from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { useDispatch, useSelector } from 'react-redux';
import { login, logoutUser, setUserLimits, setUserPlan, clearPendingClaim, selectPendingClaimUserId } from 'store/reducers/inputReducer';
import { libraryInvalidated } from 'store/reducers/libraryReducer';
import { claimAnonymousRuns } from 'store/reducers/thunks/optimizationThunks';


import get_user_plan from 'functions/server/get_user_plan.js';
import { measureOpenAiConversion } from 'utils/openaiPixel.js';
import { loadUserPlan } from 'functions/entitlements/loadUserPlan';
import { setAuthTokenGetter } from 'functions/server/authToken.js';
import { track, trackAcknowledged, EVENTS, identifyUser, resetUser } from 'utils/analytics.js';
import { getFirstTouch, hasTrackedSignup, markSignupTracked, getLastKnownPlan, setLastKnownPlan, isPlanUpgrade } from 'utils/attribution.js';

/*
A user whose Clerk account is younger than this is treated as a brand-new
sign-up.

24 hours, not the 10 minutes it used to be. Ten minutes only covers the modal
flow, where the user object appears seconds after creation. It misses the
ordinary case of signing up, being asked to verify an email, and coming back
later — those people were never counted at all. A day is safe now that the
guard below is durable and server-side: a wide window cannot double-count what
can only be marked once.
*/
/*
The conversion event name OpenAI Ads counts. It must match the event created in
their Ads Manager exactly -- a name we send that has no conversion event behind
it is accepted and then counts toward nothing, which looks identical to working.
Daniel requested this one as `sign_up`, matching the GA4/Ads name we already use.
*/
const OPENAI_SIGNUP_EVENT = "sign_up";

const NEW_USER_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * LoginSync is a component that maintains synchronization between Clerk's
 * user state and the redux login state.
 *
 * If the user is not logged in, it clears the user's limits from redux.
 *
 * This component should be included in the app's main component so that it
 * is always mounted.
 *
 * @returns {JSX.Element}
 */
const LoginSync = () => {
    const { user, isSignedIn } = useUser();
    const { getToken } = useAuth();
    const dispatch = useDispatch();
    const signedInTracked = useRef(false);
    // Guards a sign_up send that is awaiting Amplitude's acknowledgement, so a
    // re-render mid-flight cannot fire a second one. Deliberately NOT persisted:
    // see the sign_up block below for why a durable in-flight flag is a bug.
    const signupInFlight = useRef(false);
    const pendingClaimUserId = useSelector(selectPendingClaimUserId);

    // Register Clerk's session-token getter with the non-React request client so
    // every backend call can attach `Authorization: Bearer <jwt>` when signed in.
    // getToken is stable across renders; re-registering on change is idempotent.
    // The signed-in flag travels with it: it decides whether a missing token is
    // acceptable (anonymous, by design) or worth retrying (signed in, where a
    // missing token becomes a 401 once AUTH_ENFORCE is on).
    useEffect(() => {
        setAuthTokenGetter(getToken, Boolean(isSignedIn));
    }, [getToken, isSignedIn]);

    useEffect(() => {
        if (user) {
            dispatch(login({newLogin: {id: user.id}, loginSource: 'clerk'}));
            // Attribute all events to the person, and fire signed_in once per
            // session so the funnel can count sign-ups/logins as a conversion.
            identifyUser(user.id);
            if (!signedInTracked.current) {
                track(EVENTS.SIGNED_IN, {});
                signedInTracked.current = true;
            }
            /*
            sign_up fires once ever per user; returning logins keep firing
            signed_in exactly as before.

            Order matters here, and it used to be wrong. The guard was written
            BEFORE the event was sent, and track() swallows every error by
            design — so an ad blocker, a network blip, or the page unloading
            during Clerk's post-sign-up redirect left the guard claiming the
            event had fired when it never did, and it could never retry. That
            cost one of the first three real sign-ups, on the event Google Ads
            imports as the primary conversion.

            So: send first, and persist the guard only once Amplitude confirms
            it accepted the event. An unconfirmed send leaves nothing behind and
            simply retries on the person's next visit.

            The durable guard is Clerk unsafeMetadata.signupTracked, which
            survives cleared storage, a second device and incognito — localStorage
            could not, which is also why the window above can now be a full day.
            localStorage stays as a same-device fast path so two tabs racing
            inside the window cannot double-count, but it is only ever written
            after a confirmed send, so it can no longer suppress anything.

            unsafeMetadata is client-writable: marketing analysis only, never
            entitlements.
            */
            try {
                const createdAtMs = user.createdAt ? new Date(user.createdAt).getTime() : NaN;
                const isNewUser = Number.isFinite(createdAtMs) && (Date.now() - createdAtMs) < NEW_USER_WINDOW_MS;
                const alreadyCounted = Boolean(user.unsafeMetadata?.signupTracked) || hasTrackedSignup(user.id);
                if (isNewUser && !alreadyCounted && !signupInFlight.current) {
                    // Not persisted: a ref only stops this tab from sending twice
                    // while the acknowledgement is outstanding. If the send fails
                    // the next mount is free to try again.
                    signupInFlight.current = true;
                    const firstTouch = getFirstTouch();
                    // OpenAI Ads counts this conversion from the browser. Fired
                    // BEFORE the delivery check below, because it is a different
                    // destination with its own failure modes -- gating an ad
                    // platform's conversion on Amplitude acknowledging ours would
                    // make one analytics outage silently cost campaign data.
                    //
                    // The user id is the deduplication key, not a payload: a
                    // retry after a failed Amplitude send must not count the
                    // person twice, and the backend's Conversions API call can
                    // collapse onto the same id when it is built.
                    measureOpenAiConversion(OPENAI_SIGNUP_EVENT, { type: "contents" }, `signup_${user.id}`);
                    trackAcknowledged(EVENTS.SIGNED_UP, {})
                        .then((delivered) => {
                            if (!delivered) {
                                signupInFlight.current = false;
                                return;
                            }
                            markSignupTracked(user.id);
                            const metadata = { ...user.unsafeMetadata, signupTracked: new Date().toISOString() };
                            if (firstTouch && !user.unsafeMetadata?.attribution) {
                                metadata.attribution = firstTouch;
                            }
                            user.update({ unsafeMetadata: metadata })
                                .catch(() => { /* localStorage still guards this device */ });
                        })
                        .catch(() => { signupInFlight.current = false; });
                }
            } catch (e) {
                /* attribution must never break login sync */
            }
        } else  {
            dispatch(logoutUser());
            resetUser();
            signedInTracked.current = false;
        }
    }, [user, dispatch]);

    // Re-own runs the user submitted anonymously (before signing in) to their
    // account. The login reducer stashes the outgoing anon UUID as
    // pendingClaimUserId; claim it, then clear on success. On failure (e.g. the
    // endpoint isn't deployed yet) leave it to retry on the next sign-in / reload.
    useEffect(() => {
        if (user && pendingClaimUserId) {
            dispatch(claimAnonymousRuns({ anonymousUserId: pendingClaimUserId }))
                .unwrap()
                .then(() => dispatch(clearPendingClaim()))
                .catch(() => { /* best-effort; retry later */ });
        }
    }, [user, pendingClaimUserId, dispatch]);

    /* The library belongs to whoever is signed in. On any identity change -
       sign-in, sign-out, or switching accounts - drop what we are holding so the
       next reader fetches rather than briefly showing the previous account's
       saved items. Keyed on the Clerk user id, so a token refresh (same id) does
       not throw away a good list. */
    const clerkUserId = user?.id || null;
    useEffect(() => {
        dispatch(libraryInvalidated());
    }, [clerkUserId, dispatch]);

    // Load the user's plan. This previously called .then() with no .catch(), so a
    // rejected request (generic_backend_request_post throws on any non-2xx) left
    // BOTH branches unrun — and the login reducer's `free` default standing. Since
    // GET /plans publishes the tier `free`, that default matches the catalog and
    // actively locks every paid feature, so a transient 502 quietly downgraded
    // paying accounts until a later sign-in succeeded. loadUserPlan retries and,
    // if it still cannot tell, reports PLAN_UNKNOWN instead of guessing.
    useEffect(() => {
        if (!user) {
            // Signed out: clear limits. The plan is left alone — logoutUser resets
            // loginData wholesale, and writing a plan here would race that.
            dispatch(setUserLimits({newLimits: null}));
            return undefined;
        }

        // Guards against a resolved fetch for a previous user landing after a fast
        // account switch and overwriting the current user's entitlements.
        let cancelled = false;

        loadUserPlan(user.id, { fetchPlan: get_user_plan }).then(({ plan, limits, resolved }) => {
            if (cancelled) return;
            dispatch(setUserLimits({newLimits: limits}));
            dispatch(setUserPlan({plan}));

            // Everything below asserts something about the user's plan, so none of
            // it may run on PLAN_UNKNOWN.
            if (!resolved) {
                // Identify without a plan property rather than writing "unknown"
                // over a plan the profile already knows.
                identifyUser(user.id);
                return;
            }
            identifyUser(user.id, { plan });

            // Revenue-conversion proxy: the purchase happens inside Clerk's
            // checkout with no client-observable moment, so compare against
            // the last plan seen for this user and fire on free→paid. An
            // unknown previous plan is skipped, so an existing paying user
            // on a fresh browser doesn't count as an upgrade.
            //
            // Guarded by `resolved` above: an unresolved lookup would otherwise
            // satisfy previousPlan === "free" && plan !== "free" and fire a false
            // conversion, then record "unknown" and suppress the real upgrade
            // later, since previous would no longer be "free".
            try {
                const previousPlan = getLastKnownPlan(user.id);
                if (isPlanUpgrade({ previousPlan, plan, resolved })) {
                    track(EVENTS.PLAN_UPGRADED, { plan, previous_plan: previousPlan });
                }
                setLastKnownPlan(user.id, plan);
            } catch (e) {
                /* analytics must never break login sync */
            }
        });

        return () => { cancelled = true; };
    }, [user, dispatch]);
};


export default LoginSync;
